import { useEffect, useMemo, useState } from 'react';
import { useTranslations } from 'use-intl';
import type { Locale } from '@/i18n';
import {
  planIssues,
  plannedLength,
  plannedMinutes,
  substitutions,
  unusedPlayers,
} from '@shared/minutes';
import type { MatchPlanInput } from '@shared/schemas';
import { findFormation } from '@shared/formations';
import { useMatchPlan, useSaveMatchPlan } from '@/app/queries';
import { sheetName } from '@/lib/format';
import { PeriodSheets, toSheetPlayers } from '@/routes/matches/PeriodSheets';
import { Button } from '@/ui/Button';
import { Notice, Panel } from '@/ui/Panel';
import { Section } from '@/ui/Section';
import { SelectInput, TextInput } from '@/ui/Field';
import { Matrix } from '@/ui/Matrix';

export interface PlanCandidate {
  playerId: string;
  firstName: string;
  lastName: string;
  shirtNumber?: number | null;
}

/**
 * The plan as the screen holds it.
 *
 * A map and not a set, because a period answers two questions: who is on, and
 * where. It held only the first, so a coach could move a player into the
 * sixty-first minute but not into a different position — and the printed sheet,
 * reading each player's squad position, showed him where he usually plays
 * rather than where he was being put.
 *
 * `null` means "wherever he normally plays", which is the right default and the
 * one the sheet already falls back to.
 */
interface DraftPeriod {
  fromMinute: number;
  toMinute: number;
  label: string;
  /** The shape for this window. A substitution often changes it. */
  formation: string;
  players: Map<string, string | null>;
}

/**
 * The positions this shape actually has, in the order they are drawn.
 *
 * From the shape rather than the club's whole position list: a 4-2-3-1 has no
 * CB slot, and offering one would let a coach put a player somewhere the sheet
 * cannot draw him.
 */
function positionsOf(formationKey: string): string[] {
  const formation = findFormation(formationKey);
  if (!formation) return [];
  return [...new Set(formation.slots.map((slot) => slot.positionKey))];
}

const FULL_MATCH = 90;
const DEFAULT_PERIOD = 45;

function toInput(periods: DraftPeriod[]): MatchPlanInput {
  return {
    periods: periods.map((p) => ({
      fromMinute: p.fromMinute,
      toMinute: p.toMinute,
      label: p.label || undefined,
      formation: p.formation || undefined,
      // `undefined`, not `null`: the schema says a position is optional, and
      // "not stated" and "stated as nothing" are the same thing here.
      players: [...p.players].map(([playerId, positionKey]) => ({
        playerId,
        positionKey: positionKey ?? undefined,
      })),
    })),
  };
}

function forMaths(periods: DraftPeriod[]) {
  return periods.map((p) => ({
    fromMinute: p.fromMinute,
    toMinute: p.toMinute,
    players: [...p.players.keys()],
  }));
}

/**
 * Planning the minutes: the match cut into periods, an eleven in each.
 *
 * A matrix rather than a form per period. The question a coach is actually
 * holding — who has had nothing yet — is a question about the whole plan at once,
 * and a column of empty boxes answers it at a glance where four separate lists
 * would have to be held in the head and compared.
 */
export function MinutesPlan({
  eventId,
  squad,
  fixture,
  teamName,
  locale,
  baseFormation = '4-2-3-1',
  onPitch = 11,
  openByDefault = true,
}: {
  eventId: string;
  /** Match day is when a coach is actually cutting the match into periods. */
  openByDefault?: boolean;
  squad: PlanCandidate[];
  fixture: { opponent?: string | null; onDate: string; kickOff?: string | null };
  teamName?: string;
  locale: Locale;
  baseFormation?: string;
  onPitch?: number;
}) {
  const t = useTranslations();
  const plan = useMatchPlan(eventId);
  const save = useSaveMatchPlan(eventId);

  const [periods, setPeriods] = useState<DraftPeriod[]>([]);
  const [hydrated, setHydrated] = useState<string | null>(null);

  useEffect(() => {
    if (!plan.data || hydrated === eventId) return;
    setPeriods(
      plan.data.periods.map((p) => ({
        fromMinute: p.fromMinute,
        toMinute: p.toMinute,
        label: p.label ?? '',
        formation: p.formation ?? baseFormation,
        players: new Map(p.players.map((s) => [s.playerId, s.positionKey ?? null])),
      })),
    );
    setHydrated(eventId);
  }, [plan.data, eventId, hydrated]);

  const minutes = useMemo(() => {
    const lines = plannedMinutes(forMaths(periods));
    return new Map(lines.map((l) => [l.playerId, l]));
  }, [periods]);
  const issues = useMemo(() => planIssues(forMaths(periods), { onPitch }), [periods, onPitch]);
  const changes = useMemo(() => substitutions(forMaths(periods)), [periods]);
  const unused = useMemo(
    () => unusedPlayers(forMaths(periods), squad.map((p) => p.playerId)),
    [periods, squad],
  );
  const covered = plannedLength(forMaths(periods));

  const patch = (index: number, change: Partial<DraftPeriod>) =>
    setPeriods((current) => current.map((p, i) => (i === index ? { ...p, ...change } : p)));

  const toggle = (index: number, playerId: string) =>
    setPeriods((current) =>
      current.map((p, i) => {
        if (i !== index) return p;
        const players = new Map(p.players);
        if (players.has(playerId)) players.delete(playerId);
        // Null, not a guess: he goes where he normally plays until the coach
        // says otherwise, which is what the sheet already does.
        else players.set(playerId, null);
        return { ...p, players };
      }),
    );

  /** Where this player stands in this window. */
  const place = (index: number, playerId: string, positionKey: string | null) =>
    setPeriods((current) =>
      current.map((p, i) => {
        if (i !== index) return p;
        const players = new Map(p.players);
        if (!players.has(playerId)) return p;
        players.set(playerId, positionKey);
        return { ...p, players };
      }),
    );

  /**
   * Adding a period cuts the match again rather than appending to the end of it.
   *
   * The first one covers the whole match. The second has nowhere to go after it —
   * appending gave a window from 90 to 90, which is a period with no time in it —
   * so it splits the last one instead, which is what "add a period" means when
   * the match is already ninety minutes long. The eleven carries over, because a
   * substitution is a change to an eleven and not a new one.
   */
  const addPeriod = () =>
    setPeriods((current) => {
      const last = current[current.length - 1];
      if (!last) {
        return [
          {
            fromMinute: 0,
            toMinute: FULL_MATCH,
            label: '',
            formation: baseFormation,
            players: new Map<string, string | null>(),
          },
        ];
      }
      if (last.toMinute < FULL_MATCH) {
        return [
          ...current,
          {
            fromMinute: last.toMinute,
            toMinute: Math.min(FULL_MATCH, last.toMinute + DEFAULT_PERIOD),
            label: '',
            formation: last.formation,
            players: new Map(last.players),
          },
        ];
      }
      const cut = Math.round((last.fromMinute + last.toMinute) / 2);
      if (cut <= last.fromMinute) return current;
      return [
        ...current.slice(0, -1),
        { ...last, toMinute: cut },
        {
          fromMinute: cut,
          toMinute: last.toMinute,
          label: '',
          formation: last.formation,
          players: new Map(last.players),
        },
      ];
    });

  const name = (playerId: string) => {
    const p = squad.find((x) => x.playerId === playerId);
    return p ? sheetName(p.firstName, p.lastName) : playerId;
  };

  const sheetPeriods = useMemo(
    () =>
      periods.map((p) => ({
        fromMinute: p.fromMinute,
        toMinute: p.toMinute,
        formation: p.formation,
        players: toSheetPlayers(p.players, squad),
      })),
    [periods, squad],
  );

  const issueText = (issue: (typeof issues)[number]) => {
    if (issue.code === 'gap') {
      return t('minutes.issueGap', { from: issue.fromMinute, to: issue.toMinute });
    }
    if (issue.code === 'overlap') return t('minutes.issueOverlap', { at: issue.atMinute });
    if (issue.code === 'backwards') return t('minutes.issueBackwards', { at: issue.atMinute });
    return t('minutes.issueSize', { at: issue.atMinute, count: issue.count, expected: onPitch });
  };

  return (
    <Panel
      title={t('minutes.title')}
      count={periods.length ? t('minutes.covered', { minutes: covered }) : undefined}
      collapsible
      storeKey="match.minutes"
      defaultOpen={openByDefault}
    >
      <Section title={t('minutes.periods')} hint={t('minutes.hint')}>
        {periods.length === 0 ? (
          <p className="text-meta text-ink-muted">{t('minutes.empty')}</p>
        ) : (
          /*
            A grid, kept as a grid. D-048: the question a coach is holding is
            about the whole plan at once — who has had nothing yet — and a
            column of empty boxes answers it at a glance that a card per player
            would destroy. So this is `compact`: the names freeze and the
            periods scroll, rather than the 544px table that used to take the
            whole page sideways with 32px toggles in it.
          */
          <Matrix
            rows={squad}
            rowKey={(p) => p.playerId}
            mode="compact"
            caption={t('minutes.title')}
            columns={[
              {
                key: 'player',
                label: t('squad.player'),
                width: '9rem',
                cell: (p) => (
                  <span className="flex items-baseline gap-2">
                    <span className="tabular shrink-0 text-ink-faint">{p.shirtNumber ?? '–'}</span>
                    <span className="min-w-0 truncate">{sheetName(p.firstName, p.lastName)}</span>
                  </span>
                ),
              },
              ...periods.map((period, i) => ({
                key: `period-${i}`,
                width: '4.5rem',
                align: 'start' as const,
                label: (
                  <span className="grid justify-items-center gap-1">
                    <span className="flex items-center gap-1">
                      <TextInput
                        type="number"
                        min={0}
                        max={200}
                        aria-label={t('minutes.from')}
                        className="w-14 !px-1 text-center !text-meta"
                        value={period.fromMinute}
                        onChange={(e) => patch(i, { fromMinute: Number(e.target.value) })}
                      />
                      <span aria-hidden className="text-ink-faint">–</span>
                      <TextInput
                        type="number"
                        min={0}
                        max={200}
                        aria-label={t('minutes.to')}
                        className="w-14 !px-1 text-center !text-meta"
                        value={period.toMinute}
                        onChange={(e) => patch(i, { toMinute: Number(e.target.value) })}
                      />
                    </span>
                    <span
                      className={`tabular ${
                        period.players.size === onPitch ? 'text-ink-muted' : 'text-risk'
                      }`}
                    >
                      {t('minutes.onPitch', { count: period.players.size })}
                    </span>
                    <button
                      type="button"
                      className="inline-flex min-h-11 items-center text-meta text-ink-faint underline hover:text-risk"
                      onClick={() => setPeriods((current) => current.filter((_, at) => at !== i))}
                    >
                      {t('common.remove')}
                    </button>
                  </span>
                ),
                cell: (p: (typeof squad)[number]) => {
                  const on = period.players.has(p.playerId);
                  const where = period.players.get(p.playerId) ?? '';
                  const name = sheetName(p.firstName, p.lastName);
                  const window = `${period.fromMinute}–${period.toMinute}`;
                  return (
                    <span className="grid justify-items-center gap-1">
                      <button
                        type="button"
                        aria-pressed={on}
                        aria-label={`${name} ${window}`}
                        onClick={() => toggle(i, p.playerId)}
                        className={`size-11 rounded-(--radius-control) border transition-colors ${
                          on
                            ? 'border-ink bg-ink text-paper'
                            : 'border-line bg-surface text-ink-faint hover:border-ink'
                        }`}
                      >
                        {on ? '●' : ''}
                      </button>
                      {/*
                        Where, not only whether.

                        A period answers two questions and this one held only
                        the first, so a full back pushed to the wing for the
                        last twenty minutes printed at left back on the sheet —
                        the coach's actual change nowhere on the page he hands
                        out. Blank means "wherever he normally plays", which is
                        what the sheet already falls back to.
                      */}
                      {on ? (
                        <SelectInput
                          aria-label={t('minutes.positionFor', { name, window })}
                          // No height override: 44px is the floor and a dense
                          // grid is not a reason to go under it. The cell is
                          // taller for it, which is the trade the rule makes.
                          className="w-16 !px-1 text-center !text-meta"
                          value={where}
                          onChange={(e) => place(i, p.playerId, e.target.value || null)}
                        >
                          <option value="">{t('minutes.usualPosition')}</option>
                          {positionsOf(period.formation).map((key) => (
                            <option key={key} value={key}>
                              {key}
                            </option>
                          ))}
                        </SelectInput>
                      ) : null}
                    </span>
                  );
                },
              })),
              {
                key: 'minutes',
                label: t('minutes.minutes'),
                align: 'end' as const,
                width: '4rem',
                cell: (p) => {
                  const line = minutes.get(p.playerId);
                  return (
                    <span className={`tabular ${line ? 'text-ink' : 'text-risk'}`}>
                      {line ? line.minutes : 0}
                    </span>
                  );
                },
              },
            ]}
          />
        )}

        <div className="mt-4 flex flex-wrap items-center gap-2">
          <Button variant="secondary" onClick={addPeriod}>
            {t('minutes.addPeriod')}
          </Button>
          <Button
            variant="primary"
            busy={save.isPending}
            onClick={() => void save.mutateAsync(toInput(periods))}
          >
            {t('minutes.save')}
          </Button>
        </div>
        {save.isError ? (
          <div className="mt-3">
            <Notice tone="risk">{t('common.saveFailed')}</Notice>
          </div>
        ) : null}
      </Section>

      <PeriodSheets
        periods={sheetPeriods}
        fixture={fixture}
        teamName={teamName}
        locale={locale}
        onFormationChange={(index, formation) => patch(index, { formation })}
      />

      {issues.length > 0 || unused.length > 0 || changes.length > 0 ? (
        <Section title={t('minutes.whatItSays')}>
          <div className="grid gap-3">
            {changes.map((c) => (
              <p key={c.atMinute} className="text-meta">
                <span className="tabular font-semibold">{c.atMinute}&rsquo;</span>{' '}
                {c.off.length ? `${t('minutes.off')}: ${c.off.map(name).join(', ')}` : ''}
                {c.off.length && c.on.length ? ' · ' : ''}
                {c.on.length ? `${t('minutes.on')}: ${c.on.map(name).join(', ')}` : ''}
              </p>
            ))}
            {unused.length > 0 ? (
              <Notice tone="info">
                {t('minutes.nobodyYet', { names: unused.map(name).join(', ') })}
              </Notice>
            ) : null}
            {issues.map((issue, i) => (
              <Notice key={i} tone="risk">
                {issueText(issue)}
              </Notice>
            ))}
          </div>
        </Section>
      ) : null}
    </Panel>
  );
}
